import Resource from 'Resources/Resource';
import AppService from 'App/legacy/AppService';
import OnboardingResource from 'Resources/Onboarding';
import { TYPE, STYLE } from 'Store/reducers/SystemNotificationReducer';
import type AccountProfile from 'App/resources/Model/AccountProfile';

type RawAccountProfile = {
	alias: string;
	id: number;
	value: string;
};

class AccountProfileResource extends Resource {
	// The base is untyped .js, so the field it reads off `this` needs declaring here.
	eventName: string;

	constructor() {
		super('accountProfile', null);

		// Restores the two side effects ResourceHelper.onCreate/onUpdate performed in Angular: the
		// accountProfile.added / accountProfile.updated broadcast and the Created/Updated analytics
		// event. Angular tracked through SegmentHelper, Resource through globalTracker — the same
		// divergence already accepted in SoliditetClient.ts (WEB-8568).
		this.eventName = 'accountProfile';

		/*
			The constructor used to pass notificationTitle: 'admin.accountProfile', which generated a
			default set reading "Company profile / Was saved". It was dormant while nothing called
			save(); the moment save() landed it would have activated with copy Angular never showed. So
			spell out what ResourceHelper's defaults plus the overrides in
			upsales/common/resources/accountProfile.js actually produced. Only save/saveError are
			written — the generated delete/deleteError go with notificationTitle, and there is no
			delete caller.
		*/
		this.notifications = {
			save: (_data: unknown, opts: { isNew?: boolean }) => ({
				title: opts.isNew ? 'default.saved' : 'default.updated',
				body: opts.isNew ? 'saved.accountProfile' : 'default.updated',
				style: STYLE.SUCCESS,
				icon: 'check',
				type: TYPE.BODY
			}),
			saveError: (_data: unknown, opts: { isNew?: boolean }) => ({
				title: 'default.error',
				body: opts.isNew ? 'saveError.accountProfile' : 'default.error',
				style: STYLE.ERROR,
				icon: 'times',
				type: TYPE.BODY
			})
		} as any;
	}

	parse(data: RawAccountProfile | RawAccountProfile[] | AccountProfile): AccountProfile | null {
		// This is for the new domain layer that returns an array in find
		if (Array.isArray(data)) {
			return data.length ? this.parse(data[0]) : null;
		}

		// Make sure we did not get a clientParam object
		const raw = data as RawAccountProfile;
		if (data && !raw.alias && !raw.value) {
			return data as AccountProfile;
		}

		// Set profile to null if parse failed
		let json = null;
		try {
			json = JSON.parse(raw.value);
		} catch {
			// if parsing fails, the json will be null
		}

		if (json) {
			if (!json.extraColors) {
				json.extraColors = [];
			}

			if (json.typography) {
				Object.keys(json.typography).forEach(key => {
					if (!json.typography[key].fontFamily) {
						json.typography[key].fontFamily = 'Arial, Helvetica, sans-serif';
					}
				});
			}

			if (!json.colors.footerLinkText) {
				json.colors.footerLinkText = json.colors.linkText;
			}
			if (!json.colors.footerText) {
				json.colors.footerText = json.colors.mainText;
			}
		}

		return json;
	}

	new() {
		const accountSelf = AppService.getAccountSelf();

		const profile = {
			name: accountSelf.client.name,
			address: '',
			zipcode: '',
			city: '',
			country: '',
			email: '',
			phone: '',
			logo: '',
			darkLogo: '',
			btnBorderRadius: 3,
			extraColors: [{ value: '#002f65' }, { value: '#4a90e2' }, { value: '#F6F9FB' }, { value: '#E4EAF0' }],
			colors: {
				mainBg: '#002f65',
				mainText: '#2F3947',
				bodyBg: '#E4EAF0',
				contentBg: '#ffffff',
				contentText: '#6B7C93',
				contentTextIngress: '#2F3947',
				buttonBg: '#F5A623',
				buttonText: '#ffffff',
				secondaryBg: '#A4B3C7',
				secondaryText: '#002f65',
				linkText: '#4A90E2',
				footerLinkText: '#4A90E2',
				footerText: '#000000'
			},
			typography: {
				h2: {
					size: 32,
					bold: false,
					underline: false,
					italic: false,
					fontFamily: 'Arial, Helvetica, sans-serif'
				},
				h3: {
					size: 20,
					bold: false,
					underline: false,
					italic: false,
					fontFamily: 'Arial, Helvetica, sans-serif'
				},
				ingress: {
					size: 16,
					bold: false,
					underline: false,
					italic: false,
					fontFamily: 'Arial, Helvetica, sans-serif'
				},
				p: {
					size: 14,
					bold: false,
					underline: false,
					italic: false,
					fontFamily: 'Arial, Helvetica, sans-serif'
				},
				link: {
					size: 14,
					bold: false,
					underline: false,
					italic: true,
					fontFamily: 'Arial, Helvetica, sans-serif'
				}
			}
		};

		return profile;
	}

	/*
		Angular fired Onboarding.updateStep('accountProfile') from the $resource success callback and
		dropped the promise, so it is deliberately not awaited here. Awaiting super.save() first also
		reproduces the ordering against failure: on a transport error the Angular error callback ran
		instead and updateStep never fired.

		Three known divergences, all documented rather than fixed:

		1. Angular inspected response.error on an HTTP 200 body and rejected (while still calling
		   updateStep and showing the error toast). Resource.js has no envelope-error handling, so a
		   200 carrying an error envelope now resolves and shows a success toast. Standard for every
		   resource ported in this epic — do not hand-roll envelope checking here.
		2. $q's defered.reject() returns undefined, so Angular's guard below returned undefined rather
		   than a rejected promise; a caller that chained .then() on it got a TypeError. Rejecting is
		   what the guard was written to do, and no live caller passes a falsy profile.
		3. Angular ran updateStep before the toast and the accountProfile.added/updated broadcast;
		   here it runs after, because the base class owns both. All three are independent side
		   effects and the broadcast has no listeners.
	*/
	async save(profile: AccountProfile, opts = {}) {
		if (!profile) {
			return Promise.reject('No accountProfile');
		}
		const res = await super.save(profile, opts);
		OnboardingResource.updateStep('accountProfile');
		return res;
	}
}

const resource = new AccountProfileResource();

export default resource;
